import React, { Fragment, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isEmpty } from 'underscore';
import {
  Text, TextField, DefaultButton, CommandBarButton, Checkbox,
} from '@bingads-webui-react/fabric-control';
import { HelpButton } from '@bingads-webui-react/help-popup';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  RealUsedFlagSuffix,
  addAssetLibrarySurveyTriggerFlag,
  removeAssetLibrarySurveyTriggerFlag,
  AssetLibrarySurveyTriggerFlag,
  handleRealUsedFlag,
} from '@bingads-webui-campaign-react/asset-library-model';
import copilotLogoImage from './copilotLogo.png';
import squareDismissImage from './squareDismiss.svg';
import sparkleImage from './sparkle.svg';
import { statuses } from './state';
import { getAIGenerateImagesStyles } from './styles';
import { GeneratedImages, gap, imageCellHeight, imageCellWidth, marginToSelector, MaxImageCount } from './generated-images';

const ErrorString = ({ error }) => (
  <Text className="error-string" variant="small" block nowrap styles={{ root: { color: '#A80000' } }}>{error}</Text>
);

const PromptUserErrorString = withError(withCustomValidation((({ updateValidation, prompt, promptError }) => {
  useEffect(() => {
    if (promptError == null) {
      updateValidation([], prompt);
    } else {
      updateValidation([promptError], prompt);
    }
  }, [promptError, prompt, updateValidation]);

  return <Fragment />;
})));

const shouldShowEntryView = ({ state, images }) => (state.status !== statuses.loading)
  && (state.status === statuses.entry || images == null || images.length === 0);

const CopilotLogo = ({ i18n }) => (<img src={copilotLogoImage} alt={i18n.getString(_TL_('Sparkle image'))} width="256" height="256" />);

CopilotLogo.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

const SquareDismissIcon = ({ i18n }) => (<img src={squareDismissImage} alt={i18n.getString(_TL_('Square Dismiss Icon'))} width="20" height="20" />);

SquareDismissIcon.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

const SparkleIcon = ({ disabled, i18n }) => (
  <img src={sparkleImage} alt={i18n.getString(_TL_('Sparkle Icon'))} width="20" height="20" style={disabled ? { opacity: '50%' } : {}} />
);

SparkleIcon.propTypes = {
  disabled: PropTypes.bool,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

SparkleIcon.defaultProps = {
  disabled: false,
};

const BrandDetectionConfirmation = ({
  i18n, brandName, onConfirm, classes, logger,
}) => {
  const onChange = useCallback((evt, checked) => {
    if (checked && onConfirm) {
      logger.logTrace(`BrandDetectionConfirmation: confirmed to skip brand name: ${brandName}`);
      onConfirm();
    }
  }, [brandName, logger, onConfirm]);

  useEffect(() => {
    logger.logTrace(`BrandDetectionConfirmation: brand name detected: ${brandName}`);
  }, [brandName, logger]);

  return (
    <div className={classes.brandDetectionConfirmationContainer}>
      <div className="ai-warning-icon" />
      <div className="confirmation-content">
        <div className="confirmation-content-title">{i18n.getString(_TL_('Brand detected: Image contains "{{brandName}}"'), { brandName })}</div>
        <div className="confirmation-content-text">{i18n.getString(_TL_('Note: Your request for the image may contain content that is subject to the intellectual property rights of third parties.'))}</div>
        <div className="confirmation-content-action">
          <Checkbox
            label={i18n.getString(_TL_('I hereby represent and warrant that my use of the image(s) generated through my usage of Microsoft Advertising does not infringe upon the intellectual property rights of any third party.'))}
            className="confirmation-content-checkbox"
            onChange={onChange}
          />
          <HelpButton topic="pop_BA_AssetLibrary_BrandDetection" />
        </div>
      </div>
    </div>
  );
};

const SearchBar = ({
  i18n, prompt, setPrompt, isReadonly, classes, onGenerateImage,
  isValid, isMain, promptError, isCancelling, assetLibraryAccountPreferences,
}) => {
  const examplePrompt = i18n.getString(_TL_('Enter your image description'));
  const disableButton = !isValid || isReadonly || isCancelling;
  const searchRef = React.useRef(null);

  const onKeyDown = useCallback((ev) => {
    if (ev.key === 'Enter') {
      onGenerateImage();
      if (searchRef.current) {
        searchRef.current.blur();
      }
    }
  }, [onGenerateImage]);

  return (
    <>
      <div className={classNames(classes.searchBar, 'searchbar')}>
        <TextField
          readOnly={isReadonly}
          className={classNames(classes.promptInput, 'promptInput')}
          placeholder={`${examplePrompt}`}
          value={prompt || ''} // set a default value for controlled element
          onChange={(_, newValue) => {
            setPrompt(newValue);
          }}
          onKeyDown={onKeyDown}
          componentRef={searchRef}
        />
        {isMain ? null : <div className="user-error"><PromptUserErrorString prompt={prompt} promptError={promptError} /></div>}
        <DefaultButton
          disabled={disableButton}
          text={i18n.getString(_TL_('Generate images'))}
          onRenderIcon={() => (<SparkleIcon disabled={disableButton} i18n={i18n} />)}
          onClick={() => {
            handleRealUsedFlag(assetLibraryAccountPreferences, RealUsedFlagSuffix.AIGCMVP);
            onGenerateImage();
          }}
        />
      </div>
      {isMain ? <div className="user-error"><PromptUserErrorString prompt={prompt} promptError={promptError} /></div> : null}
    </>
  );
};

SearchBar.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isReadonly: PropTypes.bool.isRequired,
  isCancelling: PropTypes.bool.isRequired,
  prompt: PropTypes.string,
  promptError: PropTypes.string,
  setPrompt: PropTypes.func.isRequired,
  onGenerateImage: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    searchBar: PropTypes.string,
    promptInput: PropTypes.string,
  }).isRequired,
  isValid: PropTypes.bool.isRequired,
  isMain: PropTypes.bool.isRequired,
  assetLibraryAccountPreferences: PropTypes.shape({}),
};

SearchBar.defaultProps = {
  prompt: undefined,
  promptError: undefined,
  assetLibraryAccountPreferences: null,
};

const RemainingText = ({
  i18n, isLoading, classes,
}) => {
  const remainingText = i18n.getString(_TL_('Please note you have a limited number of image requests that we can generate quickly each day. After that, image generations may take longer than expected.'));
  return (
    <>
      <div className={classNames(classes.remainingText, 'remainingText')}>
        {isLoading ? null : <Text variant="smallPlus">{remainingText}</Text>}
      </div>
    </>
  );
};

RemainingText.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  classes: PropTypes.shape({
    remainingText: PropTypes.string,
  }).isRequired,
};

const EntryView = ({
  i18n, onGenerateImage, state, prompt, setPrompt, classes, serverError,
  isValid, promptError, assetLibraryAccountPreferences,
}) => {
  const description = i18n.getString(_TL_('Please describe the image you want to create, and Microsoft Advertising will generate multiple variations.\nFor example, "A family opening presents in front of a Christmas tree."'));
  const isCancelling = state.status === 'cancelling';
  const isLoading = state.status === 'loading';
  return (
    <div className={classNames('entry', classes.entry)}>
      <div>
        <CopilotLogo i18n={i18n} />
      </div>
      <div className={classes.header}>
        <Text variant="medium">{description}</Text>
      </div>
      <SearchBar
        i18n={i18n}
        prompt={prompt}
        setPrompt={setPrompt}
        isReadonly={isLoading}
        isValid={isValid}
        onGenerateImage={onGenerateImage}
        classes={classes}
        isMain={false}
        isCancelling={isCancelling}
        promptError={promptError}
        assetLibraryAccountPreferences={assetLibraryAccountPreferences}
      />
      {!isEmpty(serverError) && <ErrorString error={serverError} />}
      <RemainingText i18n={i18n} isLoading={isLoading} classes={classes} />
    </div>
  );
};

EntryView.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  serverError: PropTypes.string,
  prompt: PropTypes.string,
  promptError: PropTypes.string,
  setPrompt: PropTypes.func.isRequired,
  state: PropTypes.shape({
    status: PropTypes.string,
  }).isRequired,
  onGenerateImage: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    header: PropTypes.string,
    entry: PropTypes.string,
  }).isRequired,
  isValid: PropTypes.bool.isRequired,
  assetLibraryAccountPreferences: PropTypes.shape({}),
};

EntryView.defaultProps = {
  serverError: undefined,
  prompt: undefined,
  promptError: undefined,
  assetLibraryAccountPreferences: null,
};

const MainView = ({
  i18n, state, cancel, images, onGenerateImage, prompt, setPrompt, classes, onSelectImage, serverError,
  isValid, promptError, assetLibraryAccountPreferences, detectedBrandName, logger,
}) => {
  const actionText = i18n.getString(_TL_('Select the image(s) you want to display in your ad.'));
  const stopGenerate = i18n.getString(_TL_('Stop generating'));
  const isLoading = state.status === 'loading';
  const isCancelling = state.status === 'cancelling';

  const slicedImages = useMemo(() => (images && images.length ? images.slice(0, MaxImageCount) : []), [images]);

  return (
    <div className={classNames('main', classes.main)}>
      <SearchBar
        i18n={i18n}
        prompt={prompt}
        setPrompt={setPrompt}
        isReadonly={!detectedBrandName && isLoading}
        isCancelling={isCancelling}
        onGenerateImage={onGenerateImage}
        isValid={isValid}
        classes={classes}
        promptError={promptError}
        assetLibraryAccountPreferences={assetLibraryAccountPreferences}
        isMain
      />
      <RemainingText i18n={i18n} isLoading={isLoading} classes={classes} />
      {!isEmpty(serverError) && <ErrorString error={serverError} />}
      {!detectedBrandName && (
        <>
          <div className={classNames('actionSection', classes.actionSection)}>
            {isLoading ? (
              <div className={classNames('loadingSection', classes.loadingSection)}>
                <div className={classNames('loadingIndicator', classes.loadingIndicator)} />
                <CommandBarButton
                  text={stopGenerate}
                  onRenderIcon={() => (<SquareDismissIcon i18n={i18n} />)}
                  onClick={() => {
                    cancel();
                  }}
                />
              </div>
            ) : (
              <div className={classNames('actionText', classes.actionText)} >
                <Text variant="medium">{actionText}</Text>
              </div >
            )}
          </div>
          <div className={classNames('actionImages', classes.actionImages)} >
            <GeneratedImages isLoading={isLoading} images={isLoading ? [] : slicedImages} onSelectImage={onSelectImage} i18n={i18n} classes={classes} />
          </div>
          <div className={classNames('actionBottomText', classes.actionBottomText)} >
            {isLoading ? null : <Text variant="smallPlus">{i18n.getString(_TL_('Created with Microsoft Advertising'))}</Text>}
          </div>
        </>
      )}
      {detectedBrandName && (
        <BrandDetectionConfirmation
          i18n={i18n}
          brandName={detectedBrandName}
          onConfirm={() => onGenerateImage(true)}
          logger={logger}
          classes={classes}
        />
      )}
    </div>
  );
};

MainView.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  prompt: PropTypes.string,
  promptError: PropTypes.string,
  setPrompt: PropTypes.func.isRequired,
  state: PropTypes.shape({
    status: PropTypes.string,
  }).isRequired,
  serverError: PropTypes.string,
  cancel: PropTypes.func.isRequired,
  onGenerateImage: PropTypes.func.isRequired,
  images: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onSelectImage: PropTypes.func.isRequired,
  assetLibraryAccountPreferences: PropTypes.shape({}),
  classes: PropTypes.shape({
    main: PropTypes.string,
    loadingSection: PropTypes.string,
    loadingIndicator: PropTypes.string,
    actionSection: PropTypes.string,
    actionText: PropTypes.string,
    actionImages: PropTypes.string,
    actionBottomText: PropTypes.string,
  }).isRequired,
  isValid: PropTypes.bool.isRequired,
  detectedBrandName: PropTypes.string,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }),
};

MainView.defaultProps = {
  serverError: undefined,
  prompt: undefined,
  promptError: undefined,
  assetLibraryAccountPreferences: null,
  detectedBrandName: null,
  logger: { logTrace: () => null },
};

export const AssetLibraryAIGenerateImagesView = ({
  i18n, state, images, getImages, serverError, prompt, setPrompt, cancel, onSelectImage,
  isValid, promptError, logger, assetLibraryAccountPreferences,
  detectedBrandName, setDetectedBrandName, eventHandler,
}) => {
  const classes = useDefaultStyles(getAIGenerateImagesStyles({
    imageWidth: imageCellWidth, imageHeight: imageCellHeight, gap, marginToSelector,
  }));

  const onGenerateImage = useCallback((skipBrandDetection) => {
    getImages(skipBrandDetection);
    setDetectedBrandName(null);
    if (skipBrandDetection) {
      logger.logTrace(`AIGenerateImagesView: skipped detected brand name: ${detectedBrandName}`);
    }
  }, [detectedBrandName, getImages, logger, setDetectedBrandName]);

  useEffect(() => {
    if (logger) {
      logger.logTrace('AIGenerateImagesView: showed');
    }
    if (eventHandler && eventHandler.onEnterGenerateImagesPanel) {
      eventHandler.onEnterGenerateImagesPanel();
    }
    if (window.survey) {
      window.survey.AssetLibraryAppSurveyTrigger = false;
      window.survey.AssetLibraryPickerSurveyTrigger = false;
    }
    addAssetLibrarySurveyTriggerFlag(assetLibraryAccountPreferences, AssetLibrarySurveyTriggerFlag.AIGCMVP, RealUsedFlagSuffix.AIGCMVP);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    removeAssetLibrarySurveyTriggerFlag(AssetLibrarySurveyTriggerFlag.AIGCMVP);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classNames('ai-generate-image-view', classes.AIGenerateImages)}>
      {shouldShowEntryView({ state, images }) ?
        (
          <EntryView
            i18n={i18n}
            onGenerateImage={onGenerateImage}
            serverError={serverError}
            state={state}
            prompt={prompt}
            isValid={isValid}
            setPrompt={setPrompt}
            promptError={promptError}
            classes={classes}
            assetLibraryAccountPreferences={assetLibraryAccountPreferences}
          />
        ) :
        (
          <MainView
            i18n={i18n}
            serverError={serverError}
            state={state}
            cancel={cancel}
            onGenerateImage={onGenerateImage}
            detectedBrandName={detectedBrandName}
            onSelectImage={onSelectImage}
            images={images}
            isValid={isValid}
            prompt={prompt}
            setPrompt={setPrompt}
            promptError={promptError}
            classes={classes}
            assetLibraryAccountPreferences={assetLibraryAccountPreferences}
            logger={logger}
          />
        )}
    </div>
  );
};

AssetLibraryAIGenerateImagesView.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  prompt: PropTypes.string,
  setPrompt: PropTypes.func.isRequired,
  state: PropTypes.shape({
    status: PropTypes.string,
  }).isRequired,
  serverError: PropTypes.string,
  cancel: PropTypes.func.isRequired,
  getImages: PropTypes.func.isRequired,
  images: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onSelectImage: PropTypes.func.isRequired,
  promptError: PropTypes.string,
  isValid: PropTypes.bool.isRequired,
  logger: PropTypes.shape({ logTrace: PropTypes.func }),
  assetLibraryAccountPreferences: PropTypes.shape({}),
  permissions: PropTypes.shape({
    IsAssetLibraryAIGCMVPSurveyEnabled: PropTypes.bool,
  }),
  detectedBrandName: PropTypes.string,
  setDetectedBrandName: PropTypes.func.isRequired,
  eventHandler: PropTypes.shape({
    onEnterGenerateImagesPanel: PropTypes.func,
  }),
};

AssetLibraryAIGenerateImagesView.defaultProps = {
  serverError: undefined,
  prompt: undefined,
  promptError: undefined,
  logger: undefined,
  assetLibraryAccountPreferences: null,
  permissions: {},
  detectedBrandName: null,
  eventHandler: null,
};
